import { titleCase } from 'change-case'
import { Fragment } from 'react'
import { generatePath } from 'react-router-dom'
import { ToastOptions } from 'react-toastify'
import { SegmentEvents, useTrackAction } from '@opallabs/analytics'
import { pluralize } from '@opallabs/lambda'
import { Toast, Link, type ToastComponent } from '@opallabs/lapidary'
import { OpalRoutes } from '@opallabs/routing'
import { MomentData, AddToBoardToastProps, BoardOption } from '../types'

export const defaultAddToBoardSuccessOptions: ToastOptions = {
  toastId: 'add-to-board-success',
  type: 'success',
  autoClose: 2000,
  closeOnClick: true,
}

type Track = ReturnType<typeof useTrackAction>

type MainTextProps = AddToBoardToastProps & {
  track: Track
}

const linkBoardNames = (
  list: Array<BoardOption>,
  getBoardPath: (boardId: string, workspaceSlug: string) => string,
  track: Track,
  workspaceSlug: string
) =>
  list.map(({ label: boardName, value: boardId }, i) => (
    <Fragment key={boardId}>
      {list.length > 1 && i === list.length - 1 ? 'and ' : ''}
      <Link
        href={getBoardPath(boardId, workspaceSlug)}
        onClick={async (e) => {
          e.preventDefault()
          await track(SegmentEvents.BoardsNavigateBoardsSingleBoard, {
            board_id: boardId,
          })
          window.top?.location.assign(getBoardPath(boardId, workspaceSlug))
        }}
        target="_top"
      >
        {boardName}
      </Link>
      {list.length === 2 && i === 0
        ? ' '
        : list.length <= 2 || i === list.length - 1
          ? ''
          : ', '}
    </Fragment>
  ))

const getBoardPath = (boardId: string, workspaceSlug: string) =>
  generatePath(OpalRoutes.Board, { workspaceSlug, boardId })

const getStartText = (moments: MomentData[], resourceDisplayName: string) => {
  const momentCount = moments.length
  const momentTitle = momentCount === 1 ? moments[0].title : null

  return `${momentTitle ? 'The' : momentCount} ${momentTitle ? `${resourceDisplayName} ${momentTitle}` : `${resourceDisplayName}s`} ${momentCount > 1 ? 'were' : 'was'} `
}

const getAddedBoardsText = ({
  changes,
  moments,
  resourceDisplayName = 'moment',
  track,
  workspaceSlug,
}: MainTextProps) =>
  changes?.added.length ? (
    <Fragment>
      {getStartText(moments, resourceDisplayName)}
      {'added to '}
      {`${changes.added.length > 1 ? 'boards ' : 'board '}`}
      {linkBoardNames(changes.added, getBoardPath, track, workspaceSlug)}
      {changes.removed.length ? '' : '.'}
    </Fragment>
  ) : null

const getRemovedBoardsText = ({
  changes,
  moments,
  resourceDisplayName = 'moment',
  track,
  workspaceSlug,
}: MainTextProps) =>
  changes?.removed.length ? (
    <Fragment>
      {changes?.added.length
        ? ' and '
        : `${getStartText(moments, resourceDisplayName)} `}
      removed from {`${changes.removed.length > 1 ? 'boards ' : 'board '}`}
      {linkBoardNames(changes.removed, getBoardPath, track, workspaceSlug)}.
    </Fragment>
  ) : null

export const AddToBoardSuccessToast: ToastComponent<AddToBoardToastProps> = ({
  changes,
  moments,
  resourceDisplayName = 'moment',
  workspaceSlug,
  ...toastProps
}) => {
  const track = useTrackAction()
  const textProps = {
    changes,
    moments,
    resourceDisplayName,
    track,
    workspaceSlug,
  }

  return (
    <Toast
      {...toastProps}
      message={
        <Fragment>
          {getAddedBoardsText(textProps)}
          {getRemovedBoardsText(textProps)}
        </Fragment>
      }
      title={`${pluralize(titleCase(resourceDisplayName), moments.length)} Updated`}
    />
  )
}

type UnscheduleToastProps = {
  momentTitle: string
}

export const defaultUnscheduleSuccessOptions: ToastOptions = {
  toastId: 'unschedule-moment-success',
  type: 'success',
  autoClose: 2000,
  closeOnClick: true,
}

export const UnscheduleSuccessToast: ToastComponent<UnscheduleToastProps> = ({
  momentTitle,
  ...toastProps
}) => {
  return (
    <Toast
      {...toastProps}
      message={`The schedule date has been removed from the moment ${momentTitle}`}
      title="Moment Unscheduled"
    />
  )
}
